import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default feature access by tier (used as fallback)
const defaultTierFeatures = {
  none: {
    createEvent: true,
    viewDashboard: true,
    viewHelp: true,
    maxAttendees: 0,
    maxEvents: 1
  },
  basic: {
    createEvent: true,
    viewDashboard: true,
    viewAttendees: true,
    viewCommittees: true,
    viewAnnouncements: true,
    createAnnouncements: 'basic',
    viewProgram: true,
    viewHelp: true,
    maxAttendees: 30,
    maxEvents: 1,
    isDraft: true
  },
  organized: {
    createEvent: true,
    viewDashboard: true,
    viewAttendees: true,
    manageAttendees: true,
    bulkImport: true,
    viewCommittees: true,
    manageCommittees: true,
    viewAnnouncements: true,
    createAnnouncements: true,
    viewPayments: true,
    managePayments: true,
    viewDocuments: true,
    uploadDocuments: 'basic',
    viewProgram: true,
    editProgram: true,
    viewTasks: true,
    manageTasks: true,
    viewCheckin: true,
    viewReports: true,
    exportExcel: true,
    viewSupport: true,
    viewHelp: true,
    maxAttendees: 300,
    maxEvents: 1
  },
  transparency: {
    createEvent: true,
    viewDashboard: true,
    viewAttendees: true,
    manageAttendees: true,
    bulkImport: true,
    viewCommittees: true,
    manageCommittees: true,
    viewAnnouncements: true,
    createAnnouncements: true,
    viewPayments: true,
    managePayments: true,
    viewAuditLog: true,
    viewDocuments: true,
    uploadDocuments: true,
    documentVersionHistory: true,
    lockDocuments: true,
    viewProgram: true,
    editProgram: true,
    viewTasks: true,
    manageTasks: true,
    viewCheckin: true,
    qrCodeCheckin: true,
    viewReports: true,
    exportExcel: true,
    exportPdf: true,
    viewSupport: true,
    prioritySupport: true,
    viewHelp: true,
    maxAttendees: -1,
    maxEvents: 1
  },
  license: {
    all: true,
    maxAttendees: -1,
    maxEvents: -1
  }
};

// Pages/features that require specific tiers
export const featureRequirements = {
  '/organizer/attendees': { minTier: 'basic', feature: 'viewAttendees' },
  '/organizer/members': { minTier: 'basic', feature: 'viewAttendees' },
  '/organizer/committees': { minTier: 'basic', feature: 'viewCommittees' },
  '/organizer/announcements': { minTier: 'basic', feature: 'viewAnnouncements' },
  '/organizer/payments': { minTier: 'basic', feature: 'viewPayments' },
  '/organizer/documents': { minTier: 'basic', feature: 'viewDocuments' },
  '/organizer/program': { minTier: 'basic', feature: 'viewProgram' },
  '/organizer/tasks': { minTier: 'basic', feature: 'viewTasks' },
  '/organizer/checkin': { minTier: 'basic', feature: 'viewCheckin' },
  '/organizer/reports': { minTier: 'basic', feature: 'viewReports' },
  '/organizer/support': { minTier: 'basic', feature: 'viewSupport' },
};

// Tier hierarchy for comparison
const tierHierarchy = ['none', 'basic', 'organized', 'transparency', 'license'];

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTier, setCurrentTier] = useState('none');
  const [tierFeatures, setTierFeatures] = useState(defaultTierFeatures);

  useEffect(() => {
    // Fetch dynamic permissions on mount
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (token && user?.role === 'organizer') {
      fetchSubscription();
    } else {
      setLoading(false);
      setCurrentTier('none');
    }
  }, [token, user]);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/feature-permissions/public`);
      if (response.data?.permissions) {
        // Merge with defaults to ensure all base features exist
        const mergedPermissions = {
          none: { ...defaultTierFeatures.none },
          basic: { ...defaultTierFeatures.basic, ...response.data.permissions.basic },
          organized: { ...defaultTierFeatures.organized, ...response.data.permissions.organized },
          transparency: { ...defaultTierFeatures.transparency, ...response.data.permissions.transparency },
          license: { ...defaultTierFeatures.license, ...response.data.permissions.license }
        };
        setTierFeatures(mergedPermissions);
      }
    } catch (error) {
      console.log('Using default tier features');
      // Keep default features on error
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/organizer/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
      setCurrentTier(response.data?.tier || 'basic');
    } catch (error) {
      // No active subscription - default to basic (free tier)
      setCurrentTier('basic');
    } finally {
      setLoading(false);
    }
  };

  const hasFeature = (feature) => {
    if (currentTier === 'license') return true;
    const features = tierFeatures[currentTier] || tierFeatures.none;
    if (features.all) return true;
    return !!features[feature];
  };

  const getFeatureValue = (feature) => {
    if (currentTier === 'license') return true;
    const features = tierFeatures[currentTier] || tierFeatures.none;
    return features[feature];
  };

  const canAccessPage = (path) => {
    const requirement = featureRequirements[path];
    if (!requirement) return true; // No restriction
    
    // Check if user has the specific feature
    if (requirement.feature && hasFeature(requirement.feature)) {
      return true;
    }
    
    // Fall back to tier hierarchy check
    const currentTierIndex = tierHierarchy.indexOf(currentTier);
    const requiredTierIndex = tierHierarchy.indexOf(requirement.minTier);
    
    return currentTierIndex >= requiredTierIndex;
  };

  const getRequiredTierForPage = (path) => {
    const requirement = featureRequirements[path];
    return requirement?.minTier || 'none';
  };

  const isFeatureRestricted = (feature) => {
    return !hasFeature(feature);
  };

  const getTierDisplayName = (tier) => {
    const names = {
      none: 'No Subscription',
      basic: 'Basic',
      organized: 'Organized Reunion',
      transparency: 'Full Transparency',
      license: 'Organizer License'
    };
    return names[tier] || tier;
  };

  const refreshSubscription = () => {
    fetchPermissions(); // Also refresh permissions
    if (token) {
      fetchSubscription();
    }
  };

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      currentTier,
      loading,
      hasFeature,
      getFeatureValue,
      canAccessPage,
      getRequiredTierForPage,
      isFeatureRestricted,
      getTierDisplayName,
      refreshSubscription,
      tierFeatures,
      featureRequirements
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
